import { useEffect, useState } from 'react';
import CoWorkingNavBar from './CoWorkingLandingNavBar';
import { Link, useNavigate } from 'react-router-dom';
import { fetchDataFromMicroCMS } from '../utils/microCMSApi';
import { useTranslation } from 'react-i18next';

interface EventItem {
    createdAt: string;
    title_en: string;
    title_ja?: string;
    content_en?: string;
    content_ja?: string;
    tags?: { fieldId: string; tag: string }[];
    publishedAt: string;
}

export default function CoWorkingEventDetail({ params }: { params: { eventId: string } }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [eventItem, setEventItem] = useState<EventItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { eventId } = params;

    useEffect(() => {
        if (!eventId) {
            navigate("/news");
            return;
        }
        const fetchEvent = async () => {
            try {
                setLoading(true);
                const data: EventItem = await fetchDataFromMicroCMS(`events/${eventId}`);
                setEventItem(data);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchEvent();
    }, [params, navigate, eventId]);

    const langSuffix = i18n.language === 'ja' ? '_ja' : '_en';

    return (
        <div className="w-full text-[#2C2E35]">
            <CoWorkingNavBar />
            <div className="relative mt-20 text-center">
                <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] py-12">{t('cw_lp_common.events')}</h1>
            </div>

            <div className="bg-[#f6f7f9]">
                <div className="w-full max-w-7xl mx-auto p-4">
                    <div className="p-4 text-sm">
                        <Link to="/">
                            {t('cw_lp_event_1')}
                        </Link>
                        <span className="mx-2"> &gt; </span>
                        <Link to="/events">
                            {t('cw_lp_common.events')}
                        </Link>
                        <span className="mx-2"> &gt; </span>
                        <span className="text-gray-700"> {eventItem ? eventItem[`title${langSuffix}`] : ''}</span>
                    </div>

                    <div className="px-4 md:px-20 mt-20">
                        {loading ? (
                            <div className="text-center py-8">
                                <p>{t('cw_lp_common.loading')}</p>
                            </div>
                        ) : error ? (
                            <div className="text-center py-8">
                                <p>{t('cw_lp_common.error')}: {error}</p>
                            </div>
                        ) : (
                            eventItem && (
                                <>
                                    <p className="font-bold">{new Date(eventItem.publishedAt).toLocaleDateString()}</p>
                                    <h2 className="text-3xl md:text-4xl font-bold leading-[1.2] mb-4">
                                        {eventItem[`title${langSuffix}`]}
                                    </h2>
                                    <hr className="border-t border-gray-300 mt-8" />

                                    <div className="mx-4 md:mx-10">
                                        {eventItem[`content${langSuffix}`] && (
                                            <div
                                                className="rich-text max-w-full mx-auto my-8"
                                                dangerouslySetInnerHTML={{
                                                    __html: eventItem[`content${langSuffix}`] || '',
                                                }}
                                            />
                                        )}

                                        {eventItem.tags && eventItem.tags.length > 0 && (
                                            <div className="mt-8">
                                                <h3 className="text-lg font-bold">{t('cw_lp_event_5')}</h3>
                                                <div className="flex flex-wrap gap-2">
                                                    {eventItem.tags.map((tag) => (
                                                        <span key={tag.fieldId} className="border border-gray-300 px-2 py-1 text-sm rounded-md">
                                                            {tag.tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
